import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Heart } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { getImageUrl } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';

export const AuctionCard = ({ auction, showWatchlistButton = true, isInWatchlist: initialWatchlist, onWatchlistToggle }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inWatchlist, setInWatchlist] = useState(initialWatchlist || false);
  const [checkingWatchlist, setCheckingWatchlist] = useState(false);

  useEffect(() => {
    if (user && showWatchlistButton && initialWatchlist === undefined) {
      checkWatchlistStatus();
    }
  }, [user, auction.auction_id]);

  const checkWatchlistStatus = async () => {
    try {
      const response = await api.get(`/user/watchlist/check/${auction.auction_id}`);
      setInWatchlist(response.data.in_watchlist);
    } catch (error) {
      // Ignore errors silently
    }
  };

  const handleWatchlistToggle = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please log in to use watchlist');
      navigate('/login');
      return;
    }

    setCheckingWatchlist(true);
    try {
      if (inWatchlist) {
        await api.delete(`/user/watchlist/${auction.auction_id}`);
        setInWatchlist(false);
        toast.success('Removed from watchlist');
        if (onWatchlistToggle) onWatchlistToggle();
      } else {
        await api.post(`/user/watchlist/${auction.auction_id}`);
        setInWatchlist(true);
        toast.success('Added to watchlist');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update watchlist');
    } finally {
      setCheckingWatchlist(false);
    }
  };

  const handleClick = () => {
    navigate(`/auctions/${auction.auction_id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="group relative flex flex-col overflow-hidden bg-card border border-border transition-all hover:border-primary/50 cursor-pointer"
      data-testid={`auction-card-${auction.auction_id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        {auction.images && auction.images.length > 0 ? (
          <img
            src={getImageUrl(auction.images[0])}
            alt={auction.title}
            className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            No Image
          </div>
        )}
        
        {/* Status Badge */}
        {auction.status === 'sold' && (
          <div className="absolute top-3 right-3 bg-destructive text-white px-3 py-1 text-xs font-bold font-mono rounded-sm" data-testid="sold-badge">
            SOLD
          </div>
        )}
        
        {auction.status === 'active' && (
          <div className="absolute top-3 left-3">
            <div className="bg-primary text-primary-foreground px-2 py-1 text-xs font-bold font-mono rounded-sm animate-pulse" data-testid="live-badge">
              LIVE
            </div>
          </div>
        )}

        {/* Watchlist Button */}
        {showWatchlistButton && (
          <button
            onClick={handleWatchlistToggle}
            disabled={checkingWatchlist}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all ${
              inWatchlist 
                ? 'bg-red-500 text-white' 
                : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
            } ${auction.status === 'sold' ? 'top-12' : ''}`}
            data-testid={`watchlist-btn-${auction.auction_id}`}
          >
            <Heart className={`h-4 w-4 ${inWatchlist ? 'fill-current' : ''}`} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col gap-3">
        <div>
          <p className="text-xs font-mono tracking-wider uppercase text-muted-foreground mb-1">
            {auction.category}
          </p>
          <h3 className="text-lg font-semibold font-heading tracking-tight line-clamp-1" data-testid="auction-title">
            {auction.title}
          </h3>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Current Bid</p>
            <p className="text-2xl font-bold font-mono tracking-tight tabular-nums" data-testid="current-price">
              ${auction.current_price.toFixed(2)}
            </p>
          </div>
          {auction.buy_now_price && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-0.5">Buy Now</p>
              <p className="text-sm font-bold font-mono text-primary tabular-nums" data-testid="buy-now-price">
                ${auction.buy_now_price.toFixed(2)}
              </p>
            </div>
          )}
        </div>

        {auction.status === 'active' && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span>{auction.bid_count} bids</span>
            </div>
            <CountdownTimer endDate={auction.end_date} />
          </div>
        )}
      </div>
    </div>
  );
};
